// EmptyVouchers — v1.13.67
//
// Controller station: record how many empties a person handed in and issue
// a bearer voucher (physical slip) they redeem at POS later. Anyone can
// return — no customer registration required. Sales Report + Cash Book
// stay untouched (empties are stock, not money).
//
// Voucher lifecycle:
//   ACTIVE → CLAIMED (fully redeemed at POS)
//   ACTIVE → VOID    (admin void, e.g. lost slip)
// Partial redemption is supported: qty_remaining drops per claim; status
// flips to CLAIMED only when it hits 0.

import React, { useEffect, useState } from 'react';
import { FiRefreshCw, FiPackage, FiPlus, FiX, FiSearch, FiPrinter } from 'react-icons/fi';
import { getProducts } from '../services/api';
import fmtInvoiceNo from '../utils/fmtInvoiceNo';
import { useAuth } from '../context/AuthContext';
import AdminPasswordPrompt from '../components/AdminPasswordPrompt';
import {
  listEmptyVouchers, listEmptyVoucherClaims,
  getEmptyVoucher, createEmptyVoucher, voidEmptyVoucher,
} from '../services/api';

const STATUS_META = {
  ACTIVE:  { label: 'Active',  bg: '#dcfce7', color: '#166534', border: '#86efac' },
  CLAIMED: { label: 'Claimed', bg: '#dbeafe', color: '#1d4ed8', border: '#93c5fd' },
  VOID:    { label: 'Void',    bg: '#fef2f2', color: '#b91c1c', border: '#fecaca' },
};

const fmtDate = (s) => s ? new Date(s.replace(' ', 'T') + 'Z').toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

export default function EmptyVouchers() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'Administrator' || user?.role === 'Admin';

  const [statusFilter, setStatusFilter] = useState('ACTIVE');
  const [query, setQuery]     = useState('');
  // 2026-10-21 — date range filter. Both inclusive, YYYY-MM-DD.
  // Defaults to today → today so the page lands on the daily report
  // view; cashier changes the range or clears it to go wider.
  const _today = new Date().toISOString().slice(0, 10);
  const [fromDate, setFromDate] = useState(_today);
  const [toDate,   setToDate]   = useState(_today);
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast]     = useState(null);
  const [issueModal, setIssueModal] = useState(null);  // { qty, name, phone, notes, saving, error, slipText, voucher }
  const [detail, setDetail]         = useState(null);  // { voucher, claims }
  const [voidModal, setVoidModal]   = useState(null);  // { voucher, reason, saving, error }
  // 2026-10-21 — admin-password gate on voiding. { voucher, reason, perform }
  const [pendingVoid, setPendingVoid] = useState(null);
  // 2026-08-30 — the empties you can hand in. Same Crates category the GRN
  // credit-note picker uses, so the two agree on what counts as an empty.
  const [crateProducts, setCrateProducts] = useState([]);
  useEffect(() => {
    let alive = true;
    getProducts()
      .then(({ data }) => {
        const all = Array.isArray(data) ? data : (data?.products || []);
        // 2026-10-29 — Also include "empties" category (B BOX, B BOTTLE etc.)
        // so cashier can issue a voucher for a split return (box only or
        // bottle only). State var still called `crateProducts` for history —
        // it now holds both Crates and Empties categories.
        if (alive) setCrateProducts(all.filter(p => {
          const cat = (p.category_name || '').toLowerCase();
          return cat === 'crates' || cat === 'empties';
        }));
      })
      .catch(() => { /* picker just stays empty; the form explains why */ });
    return () => { alive = false; };
  }, []);

  const flash = (text, type = 'ok') => {
    setToast({ text, type });
    setTimeout(() => setToast(null), type === 'err' ? 5000 : 2800);
  };

  // 2026-10-21 — the Claimed tab now lists individual claim events
  // (one row per voucher draw) instead of only fully-claimed vouchers.
  // Separate state so the row shape is tracked, and so switching tabs
  // doesn't briefly render one shape in the other's table.
  const [claimRows, setClaimRows] = useState([]);
  const showClaims = statusFilter === 'CLAIMED';

  const load = async () => {
    setLoading(true);
    try {
      const dateParams = {
        from: fromDate || undefined,
        to:   toDate   || undefined,
      };
      if (showClaims) {
        const { data } = await listEmptyVoucherClaims({ limit: 500, ...dateParams });
        const all = Array.isArray(data) ? data : [];
        const q = query.trim().toLowerCase();
        setClaimRows(q
          ? all.filter(r =>
              (r.voucher_number || '').toLowerCase().includes(q)
              || (r.product_name || '').toLowerCase().includes(q)
              || (r.order_number || '').toLowerCase().includes(q)
              || (r.issued_to_name || '').toLowerCase().includes(q)
              || (r.issued_to_phone || '').includes(q))
          : all);
        setRows([]);
      } else {
        const { data } = await listEmptyVouchers({
          status: statusFilter || undefined,
          q: query.trim() || undefined,
          limit: 300,
          ...dateParams,
        });
        setRows(Array.isArray(data) ? data : []);
        setClaimRows([]);
      }
    } catch (e) {
      flash(e?.response?.data?.error || e.message, 'err');
    }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter, fromDate, toDate]);
  useEffect(() => {
    const id = setTimeout(load, 250);
    return () => clearTimeout(id);
    // eslint-disable-next-line
  }, [query]);

  // ── Issue ──────────────────────────────────────────────────────
  const openIssue = () => setIssueModal({ qty: '', product_sync_id: '', name: '', phone: '', notes: '', saving: false, error: null, slipText: null, voucher: null });

  const submitIssue = async () => {
    const q = parseInt(issueModal.qty, 10) || 0;
    if (!(q > 0)) { setIssueModal(m => ({ ...m, error: 'Qty must be > 0.' })); return; }
    if (!issueModal.product_sync_id) {
      setIssueModal(m => ({ ...m, error: 'Pick which empty was returned.' })); return;
    }
    setIssueModal(m => ({ ...m, saving: true, error: null }));
    try {
      const { data } = await createEmptyVoucher({
        qty: q,
        product_sync_id: issueModal.product_sync_id,
        issued_to_name: issueModal.name || null,
        issued_to_phone: issueModal.phone || null,
        notes: issueModal.notes || null,
      });
      setIssueModal(m => ({ ...m, saving: false, voucher: data.voucher, slipText: data.slip_text }));
      flash(`Voucher ${data.voucher.voucher_number} issued.`);
      load();
    } catch (e) {
      setIssueModal(m => ({ ...m, saving: false, error: e?.response?.data?.error || e.message }));
    }
  };

  const printSlip = (text) => {
    const w = window.open('', '', 'width=400,height=600');
    if (!w) return;
    w.document.write(`<pre style="font-family: 'Courier New', monospace; font-size: 12px; padding: 8px;">${text.replace(/</g, '&lt;')}</pre>`);
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); w.close(); }, 100);
  };

  // ── Detail ────────────────────────────────────────────────────
  const openDetail = async (row) => {
    try {
      const { data } = await getEmptyVoucher(row.voucher_number);
      setDetail(data);
    } catch (e) {
      flash(e?.response?.data?.error || e.message, 'err');
    }
  };

  // ── Void ──────────────────────────────────────────────────────
  const openVoid = (voucher) => setVoidModal({ voucher, reason: '', saving: false, error: null });

  const submitVoid = () => {
    const reason = voidModal.reason.trim();
    if (!reason) { setVoidModal(m => ({ ...m, error: 'Reason is required.' })); return; }
    // 2026-10-21 — admin-password gate (voiding reverses stock + blocks
    // future claims — one-click confirm on just the void modal is too
    // easy to mis-fire).
    const voucher = voidModal.voucher;
    setPendingVoid({
      voucher, reason,
      perform: async () => {
        setVoidModal(m => (m ? { ...m, saving: true, error: null } : m));
        try {
          await voidEmptyVoucher(voucher.id, reason);
          flash(`Voucher ${voucher.voucher_number} voided.`);
          setVoidModal(null);
          setDetail(null);
          load();
        } catch (e) {
          setVoidModal(m => (m ? { ...m, saving: false, error: e?.response?.data?.error || e.message } : m));
        }
      },
    });
  };

  const confirmVoid = async () => {
    const job = pendingVoid;
    setPendingVoid(null);
    if (job?.perform) await job.perform();
  };

  // ── Report ────────────────────────────────────────────────────
  // 2026-10-21 — daily operations summary. Independent of the active
  // tab: fetches BOTH vouchers (received) and claims (redeemed) over
  // the current date range, then pivots per empty type into
  //   Received · Claimed · Remaining (= Received − Claimed in range).
  // Detail of each is listed under the summary.
  const printReport = async () => {
    const esc = (s) => String(s ?? '').replace(/[&<>]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));
    const dateParams = { from: fromDate || undefined, to: toDate || undefined };
    let vouchers = [], claims = [];
    try {
      const [vRes, cRes] = await Promise.all([
        listEmptyVouchers({ limit: 1000, ...dateParams }),
        listEmptyVoucherClaims({ limit: 1000, ...dateParams }),
      ]);
      vouchers = Array.isArray(vRes.data) ? vRes.data : [];
      claims   = Array.isArray(cRes.data) ? cRes.data : [];
    } catch (e) {
      flash(e?.response?.data?.error || e.message, 'err');
      return;
    }

    const sameDay = fromDate && fromDate === toDate;
    const dateLbl = (() => {
      if (sameDay) return fromDate;
      const parts = [];
      if (fromDate) parts.push(`from ${fromDate}`);
      if (toDate)   parts.push(`to ${toDate}`);
      return parts.length ? parts.join(' ') : 'All dates';
    })();
    const nowStr = new Date().toLocaleString();
    const title = `Empty Vouchers — Daily Report`;

    // Pivot by empty type. VOID vouchers are excluded from "received"
    // (they never counted as stock in).
    const stripEmpty = (n) => (n || 'Unspecified').replace(/^EMPTY\s*/i, '').trim() || 'Unspecified';
    const byType = {};
    const bump = (name, field, qty) => {
      const k = stripEmpty(name);
      if (!byType[k]) byType[k] = { received: 0, claimed: 0 };
      byType[k][field] += qty;
    };
    vouchers.filter(v => v.status !== 'VOID').forEach(v =>
      bump(v.product_name, 'received', parseInt(v.qty_original, 10) || 0));
    claims.forEach(c =>
      bump(c.product_name, 'claimed', parseInt(c.qty_claimed, 10) || 0));

    const typeRows = Object.entries(byType).sort((a, b) => a[0].localeCompare(b[0]));
    const totalReceived  = typeRows.reduce((s, [, v]) => s + v.received, 0);
    const totalClaimed   = typeRows.reduce((s, [, v]) => s + v.claimed, 0);
    const totalRemaining = totalReceived - totalClaimed;
    const voidCount = vouchers.filter(v => v.status === 'VOID').length;

    const summaryHead = `
      <tr>
        <th>Empty</th>
        <th class="r">Received</th>
        <th class="r">Claimed</th>
        <th class="r">Remaining</th>
      </tr>`;
    const summaryBody = typeRows.length === 0
      ? `<tr><td colspan="4" style="text-align:center;color:#9ca3af;padding:14px">No activity in this range.</td></tr>`
      : typeRows.map(([name, v]) => `<tr>
          <td>${esc(name)}</td>
          <td class="r">${v.received}</td>
          <td class="r">${v.claimed}</td>
          <td class="r"><b>${v.received - v.claimed}</b></td>
        </tr>`).join('');
    const summaryFoot = `<tr class="total">
        <td>TOTAL</td>
        <td class="r">${totalReceived}</td>
        <td class="r">${totalClaimed}</td>
        <td class="r">${totalRemaining}</td>
      </tr>`;

    // Detail: vouchers issued in range (received), claims drawn in range.
    const receivedRows = vouchers.filter(v => v.status !== 'VOID').map(v => `<tr>
        <td>${esc(v.voucher_number)}</td>
        <td>${esc(stripEmpty(v.product_name))}</td>
        <td class="r">${esc(v.qty_original)}</td>
        <td class="r">${esc(v.qty_remaining)}</td>
        <td>${esc(STATUS_META[v.status]?.label || v.status)}</td>
        <td>${esc(v.issued_to_name || '—')}${v.issued_to_phone ? `<br><span class="muted">${esc(v.issued_to_phone)}</span>` : ''}</td>
        <td>${esc(fmtDate(v.issued_at))}</td>
      </tr>`).join('');

    const claimedRows = claims.map(c => {
      const sdcSuffix = String(c.zra_sdc_id || '').replace(/^SDC/i, '');
      const zraRef = (sdcSuffix && c.zra_rcpt_no) ? `INV${sdcSuffix}/${c.zra_rcpt_no}` : '';
      return `<tr>
        <td>${esc(c.voucher_number)}</td>
        <td>${esc(stripEmpty(c.product_name))}</td>
        <td class="r">${esc(c.qty_claimed)}</td>
        <td>${zraRef ? `<b>${esc(zraRef)}</b><br>` : ''}<span class="muted">${esc(c.order_number || '—')}</span></td>
        <td>${esc(c.issued_to_name || '—')}</td>
        <td>${esc(fmtDate(c.created_at))}</td>
      </tr>`;
    }).join('');

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${esc(title)}</title>
      <style>
        @page { size: A4 landscape; margin: 12mm; }
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: 'Segoe UI', Arial, sans-serif; font-size: 11px; color: #111; }
        h1 { font-size: 18px; font-weight: 800; }
        h2 { font-size: 13px; font-weight: 700; margin: 18px 0 6px; color: #111; }
        .sub { font-size: 11px; color: #555; margin-top: 2px; }
        table { width: 100%; border-collapse: collapse; margin-top: 4px; }
        th, td { padding: 6px 8px; border-bottom: 1px solid #ddd; text-align: left; vertical-align: top; }
        th { background: #f3f4f6; font-weight: 700; font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; }
        td.r, th.r { text-align: right; }
        .muted { color: #6b7280; font-size: 10px; }
        tr.total td { background: #eef2ff; font-weight: 800; border-top: 1.5px solid #000; border-bottom: none; }
        .cards { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; margin: 12px 0 6px; }
        .card { border: 1px solid #e5e7eb; border-radius: 6px; padding: 8px 10px; }
        .card .k { font-size: 9px; text-transform: uppercase; letter-spacing: 0.5px; color: #6b7280; font-weight: 700; }
        .card .v { font-size: 18px; font-weight: 800; margin-top: 2px; }
        .foot { margin-top: 14px; display: flex; justify-content: space-between; font-size: 10px; color: #555; border-top: 1px solid #ddd; padding-top: 6px; }
      </style></head><body>
      <h1>${esc(title)}</h1>
      <div class="sub">${esc(dateLbl)}${voidCount ? ` · ${voidCount} void voucher${voidCount === 1 ? '' : 's'} excluded` : ''}</div>

      <div class="cards">
        <div class="card"><div class="k">Received</div><div class="v" style="color:#166534">${totalReceived}</div></div>
        <div class="card"><div class="k">Claimed</div><div class="v" style="color:#1d4ed8">${totalClaimed}</div></div>
        <div class="card"><div class="k">Remaining (received − claimed)</div><div class="v" style="color:#0e7490">${totalRemaining}</div></div>
      </div>

      <h2>Summary by empty type</h2>
      <table>
        <thead>${summaryHead}</thead>
        <tbody>${summaryBody}</tbody>
        <tfoot>${summaryFoot}</tfoot>
      </table>

      <h2>Received — ${vouchers.filter(v => v.status !== 'VOID').length} voucher${vouchers.filter(v => v.status !== 'VOID').length === 1 ? '' : 's'}</h2>
      ${receivedRows ? `<table>
        <thead><tr>
          <th>Voucher #</th><th>Empty</th>
          <th class="r">Original</th><th class="r">Still on voucher</th>
          <th>Status</th><th>Issued to</th><th>Issued at</th>
        </tr></thead>
        <tbody>${receivedRows}</tbody>
      </table>` : `<div class="muted" style="padding:8px 0">No vouchers issued in this range.</div>`}

      <h2>Claimed — ${claims.length} claim${claims.length === 1 ? '' : 's'}</h2>
      ${claimedRows ? `<table>
        <thead><tr>
          <th>Voucher #</th><th>Empty</th>
          <th class="r">Qty</th><th>Invoice</th>
          <th>Issued to</th><th>Claimed at</th>
        </tr></thead>
        <tbody>${claimedRows}</tbody>
      </table>` : `<div class="muted" style="padding:8px 0">No claims in this range.</div>`}

      <div class="foot"><span>Printed ${esc(nowStr)}</span><span>Alem POS</span></div>
    </body></html>`;
    const w = window.open('', '_blank');
    if (w) { w.document.write(html); w.document.close(); w.focus(); setTimeout(() => w.print(), 300); }
  };

  // ── Stats ─────────────────────────────────────────────────────
  // 2026-10-21 — the top cards show ACTIVE voucher counts across the
  // whole book, not just the current tab. Keeping a separate state so
  // the Claimed / Void / All tabs still show accurate stats.
  const [activeVouchers, setActiveVouchers] = useState([]);
  useEffect(() => {
    let alive = true;
    listEmptyVouchers({ status: 'ACTIVE', limit: 1000 })
      .then(({ data }) => { if (alive) setActiveVouchers(Array.isArray(data) ? data : []); })
      .catch(() => { if (alive) setActiveVouchers([]); });
    return () => { alive = false; };
    // Re-fetch when the row list changes (issue / void / claim refund) so
    // the totals catch up without a page refresh.
  }, [rows, claimRows]);
  const activeCount     = activeVouchers.length;
  const activeRemaining = activeVouchers.reduce((s, r) => s + (parseInt(r.qty_remaining, 10) || 0), 0);

  return (
    <div style={{ padding: 24, background: '#f8fafc', height: '100vh', overflowY: 'auto', boxSizing: 'border-box' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#111827' }}>Empty Vouchers</h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6b7280' }}>
            Controller station — record empties returned and issue bearer slips redeemable at POS.
            Physical stock only, doesn't hit Sales Report or Cash Book.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={load}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 16px', borderRadius: 8, border: '1.5px solid #e5e7eb', background: '#fff', color: '#374151', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
            <FiRefreshCw size={14} /> Refresh
          </button>
          <button onClick={printReport}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 16px', borderRadius: 8, border: '1.5px solid #c7d2fe', background: '#eef2ff', color: '#3730a3', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
            <FiPrinter size={14} /> Print Report
          </button>
          <button onClick={openIssue}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 18px', borderRadius: 8, border: 'none', background: '#16a34a', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
            <FiPlus size={14} /> Issue Voucher
          </button>
        </div>
      </div>

      {toast && (
        <div style={{ padding: '10px 14px', marginBottom: 14, borderRadius: 8, fontSize: 13,
                       background: toast.type === 'ok' ? '#f0fdf4' : '#fef2f2',
                       color:      toast.type === 'ok' ? '#166534' : '#b91c1c',
                       border: `1px solid ${toast.type === 'ok' ? '#86efac' : '#fecaca'}` }}>
          {toast.text}
        </div>
      )}

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14, marginBottom: 18 }}>
        <div style={{ padding: 14, borderRadius: 10, background: '#fff', border: '1px solid #e5e7eb' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 6 }}>Active vouchers</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: '#166534' }}>{activeCount.toLocaleString()}</div>
        </div>
        <div style={{ padding: 14, borderRadius: 10, background: '#fff', border: '1px solid #e5e7eb' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 6 }}>Total empties held</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: '#0e7490' }}>{activeRemaining.toLocaleString()}</div>
          {/* Broken down per empty: a single total across EMPTY ZB, EMPTY
              500ML and crates is not a number anyone can act on. */}
          {(() => {
            const byProduct = {};
            rows.filter(r => r.status === 'ACTIVE').forEach(r => {
              const k = r.product_name || 'Unspecified';
              byProduct[k] = (byProduct[k] || 0) + (parseInt(r.qty_remaining, 10) || 0);
            });
            const entries = Object.entries(byProduct).filter(([, n]) => n > 0)
              .sort((a, b) => b[1] - a[1]);
            if (entries.length === 0) return null;
            return (
              <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 3 }}>
                {entries.map(([name, n]) => (
                  <div key={name} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#475569' }}>
                    <span>{name}</span><strong>{n.toLocaleString()}</strong>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, alignItems: 'center', flexWrap: 'wrap' }}>
        {['ACTIVE', 'CLAIMED', 'VOID', ''].map(s => {
          const label = s ? STATUS_META[s].label : 'All';
          const active = statusFilter === s;
          return (
            <button key={s || 'all'}
              onClick={() => setStatusFilter(s)}
              style={{ padding: '7px 14px', borderRadius: 8, fontSize: 12, fontWeight: 700,
                       border: active ? '1.5px solid #111827' : '1.5px solid #e5e7eb',
                       background: active ? '#111827' : '#fff',
                       color: active ? '#fff' : '#374151',
                       cursor: 'pointer' }}>
              {label}
            </button>
          );
        })}
        <div style={{ position: 'relative', flex: 1, minWidth: 220 }}>
          <FiSearch size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
          <input type="text" value={query} onChange={e => setQuery(e.target.value)}
                 placeholder="Search by voucher #, name, or phone…"
                 style={{ width: '100%', padding: '9px 12px 9px 32px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }} />
        </div>
        {/* 2026-10-21 — date range. On vouchers tab filters issued_at;
            on Claimed tab filters claim created_at. */}
        <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>From</span>
        <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
          style={{ padding: '7px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13 }} />
        <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>To</span>
        <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
          style={{ padding: '7px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13 }} />
        {(fromDate || toDate) && (
          <button onClick={() => { setFromDate(''); setToDate(''); }}
            style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid #e5e7eb', background: '#fff', fontSize: 11, cursor: 'pointer', color: '#6b7280' }}>
            Clear dates
          </button>
        )}
      </div>

      {/* List */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading…</div>
        ) : showClaims ? (
          claimRows.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>No claims yet.</div>
          ) : (
            <table className="phone-cards" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f9fafb', color: '#374151', fontSize: 11, textTransform: 'uppercase' }}>
                  <th style={th}>Voucher #</th>
                  <th style={th}>Empty</th>
                  <th style={{ ...th, textAlign: 'right' }}>Qty claimed</th>
                  <th style={th}>Invoice</th>
                  <th style={th}>Issued to</th>
                  <th style={th}>Claimed at</th>
                </tr>
              </thead>
              <tbody>
                {claimRows.map(r => {
                  // 2026-10-21 — ZRA composite = INV<sdcNumeric>/<rcptNo>,
                  // matching the Sales Report layout. Shown on top; the
                  // internal INV-… number sits below it as a smaller row.
                  const sdcSuffix = String(r.zra_sdc_id || '').replace(/^SDC/i, '');
                  const zraRef = (sdcSuffix && r.zra_rcpt_no) ? `INV${sdcSuffix}/${r.zra_rcpt_no}` : null;
                  return (
                    <tr key={r.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                      <td style={{ ...td, fontFamily: 'monospace', fontWeight: 700 }}>{r.voucher_number}</td>
                      <td style={td}>{(r.product_name || '').replace(/^EMPTY\s*/i, '') || <span style={{ color: '#9ca3af' }}>—</span>}</td>
                      <td style={{ ...td, textAlign: 'right', fontWeight: 800, color: '#1d4ed8' }}>{r.qty_claimed}</td>
                      <td style={{ ...td, fontFamily: 'monospace' }}>
                        {zraRef && <div style={{ fontWeight: 700 }}>{zraRef}</div>}
                        <div style={{ fontSize: 11, color: zraRef ? '#6b7280' : '#111827', fontWeight: zraRef ? 400 : 700 }}>
                          {r.order_number || <span style={{ color: '#9ca3af' }}>—</span>}
                        </div>
                      </td>
                      <td style={td}>
                        {r.issued_to_name || <span style={{ color: '#9ca3af' }}>—</span>}
                        {r.issued_to_phone && <div style={{ fontSize: 11, color: '#6b7280' }}>{r.issued_to_phone}</div>}
                      </td>
                      <td style={{ ...td, color: '#6b7280', fontSize: 12 }}>
                        {fmtDate(r.created_at)}
                        {r.claimed_by_name && <div style={{ fontSize: 11 }}>by {r.claimed_by_name}</div>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )
        ) : rows.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>No vouchers.</div>
        ) : (
          <table className="phone-cards" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f9fafb', color: '#374151', fontSize: 11, textTransform: 'uppercase' }}>
                <th style={th}>Voucher #</th>
                <th style={th}>Issued to</th>
                <th style={{ ...th, textAlign: 'right' }}>Original</th>
                <th style={{ ...th, textAlign: 'right' }}>Remaining</th>
                <th style={th}>Status</th>
                <th style={th}>Issued</th>
                <th style={{ ...th, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const meta = STATUS_META[r.status] || STATUS_META.ACTIVE;
                return (
                  <tr key={r.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                    <td style={{ ...td, fontFamily: 'monospace', fontWeight: 700 }}>{r.voucher_number}</td>
                    <td style={td}>
                      {r.issued_to_name || <span style={{ color: '#9ca3af' }}>—</span>}
                      {r.issued_to_phone && <div style={{ fontSize: 11, color: '#6b7280' }}>{r.issued_to_phone}</div>}
                    </td>
                    <td style={{ ...td, textAlign: 'right' }}>{r.qty_original}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 800, color: r.qty_remaining > 0 ? '#0e7490' : '#94a3b8' }}>{r.qty_remaining}</td>
                    <td style={td}>
                      <span style={{ padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700, background: meta.bg, color: meta.color, border: `1px solid ${meta.border}` }}>
                        {meta.label}
                      </span>
                    </td>
                    <td style={{ ...td, color: '#6b7280', fontSize: 12 }}>
                      {fmtDate(r.issued_at)}
                      {r.issued_by_name && <div style={{ fontSize: 11 }}>by {r.issued_by_name}</div>}
                    </td>
                    <td style={{ ...td, textAlign: 'right' }}>
                      <button onClick={() => openDetail(r)}
                        style={{ padding: '5px 12px', borderRadius: 6, border: '1.5px solid #e5e7eb', background: '#fff', color: '#374151', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                        View
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Issue modal */}
      {issueModal && (
        <div style={overlay} onClick={() => !issueModal.saving && setIssueModal(null)}>
          <div style={{ ...modal, maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <ModalHeader title={issueModal.voucher ? `Voucher ${issueModal.voucher.voucher_number}` : 'Issue Empty Voucher'}
                         subtitle={issueModal.voucher ? 'Hand this slip to the customer.' : 'Record how many empties the person handed in.'}
                         onClose={() => setIssueModal(null)} />
            <div style={{ padding: '18px 22px' }}>
              {issueModal.voucher ? (
                <>
                  <pre style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: 14, fontFamily: '"Courier New", monospace', fontSize: 12, whiteSpace: 'pre', overflowX: 'auto' }}>
                    {issueModal.slipText}
                  </pre>
                  <button onClick={() => printSlip(issueModal.slipText)}
                    style={{ marginTop: 12, display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', border: '1.5px solid #0ea5e9', borderRadius: 6, background: '#fff', color: '#0369a1', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
                    <FiPrinter size={14} /> Print slip
                  </button>
                </>
              ) : (
                <>
                  {/* Which empty. Asked BEFORE the quantity, because "12"
                      means nothing until you know 12 of what. */}
                  <Field label="Which empty *">
                    <select value={issueModal.product_sync_id} autoFocus
                            onChange={e => setIssueModal(m => ({ ...m, product_sync_id: e.target.value }))}
                            style={inp}>
                      <option value="">— pick the empty —</option>
                      {crateProducts.map(p => (
                        <option key={p.sync_id} value={p.sync_id}>{p.name}</option>
                      ))}
                    </select>
                    {crateProducts.length === 0 && (
                      <div style={{ fontSize: 11, color: '#b45309', marginTop: 4 }}>
                        No products in the <strong>Crates</strong> category yet — ask HQ to add one under Item Details.
                      </div>
                    )}
                  </Field>
                  <Field label="Empties returned *">
                    <input type="number" min="1" step="1" value={issueModal.qty}
                           onChange={e => setIssueModal(m => ({ ...m, qty: e.target.value }))}
                           placeholder="e.g. 12" style={inp} />
                  </Field>
                  <Field label="Issued to (name — optional)">
                    <input type="text" value={issueModal.name}
                           onChange={e => setIssueModal(m => ({ ...m, name: e.target.value }))}
                           placeholder="e.g. Sirak Solomon" style={inp} />
                  </Field>
                  <Field label="Phone (optional)">
                    <input type="text" value={issueModal.phone}
                           onChange={e => setIssueModal(m => ({ ...m, phone: e.target.value }))}
                           placeholder="e.g. 097XXX" style={inp} />
                  </Field>
                  <Field label="Notes (optional)">
                    <input type="text" value={issueModal.notes}
                           onChange={e => setIssueModal(m => ({ ...m, notes: e.target.value }))}
                           placeholder="e.g. Truck delivery" style={inp} />
                  </Field>
                  {issueModal.error && (
                    <div style={{ padding: '10px 12px', background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: 8, fontSize: 12, marginTop: 10 }}>
                      {issueModal.error}
                    </div>
                  )}
                </>
              )}
            </div>
            <div style={{ padding: '14px 22px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => !issueModal.saving && setIssueModal(null)}
                      style={btnSecondary}>
                {issueModal.voucher ? 'Close' : 'Cancel'}
              </button>
              {!issueModal.voucher && (
                <button onClick={submitIssue} disabled={issueModal.saving} style={btnPrimary(issueModal.saving)}>
                  {issueModal.saving ? 'Issuing…' : 'Issue Voucher'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Detail modal */}
      {detail && (
        <div style={overlay} onClick={() => setDetail(null)}>
          <div style={{ ...modal, maxWidth: 640, maxHeight: '85vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
            <ModalHeader title={detail.voucher.voucher_number}
                         subtitle={`${detail.voucher.qty_original} original · ${detail.voucher.qty_remaining} remaining · ${STATUS_META[detail.voucher.status]?.label}`}
                         onClose={() => setDetail(null)} />
            <div style={{ padding: '18px 22px', overflowY: 'auto', flex: 1 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14, fontSize: 12 }}>
                <ProvField label="Issued to">{detail.voucher.issued_to_name || <span style={{ color: '#9ca3af' }}>—</span>}</ProvField>
                <ProvField label="Phone">{detail.voucher.issued_to_phone || <span style={{ color: '#9ca3af' }}>—</span>}</ProvField>
                <ProvField label="Issued at">{fmtDate(detail.voucher.issued_at)}</ProvField>
                <ProvField label="Issued by">{detail.voucher.issued_by_name || '—'}</ProvField>
                {detail.voucher.notes && <ProvField label="Notes" span>{detail.voucher.notes}</ProvField>}
                {detail.voucher.status === 'VOID' && (
                  <ProvField label="Void reason" span>
                    <span style={{ color: '#b91c1c' }}>{detail.voucher.void_reason}</span>
                    <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                      {fmtDate(detail.voucher.void_at)} · by {detail.voucher.void_by_name || '—'}
                    </div>
                  </ProvField>
                )}
              </div>
              <h4 style={{ margin: '18px 0 8px', fontSize: 13, fontWeight: 700, color: '#111827' }}>Claim history ({detail.claims.length})</h4>
              {detail.claims.length === 0 ? (
                <div style={{ padding: 16, textAlign: 'center', color: '#9ca3af', fontSize: 12, border: '1px dashed #e5e7eb', borderRadius: 8 }}>No claims yet.</div>
              ) : (
                <table className="phone-cards" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: '#f9fafb', color: '#374151', fontSize: 11, textTransform: 'uppercase' }}>
                      <th style={th}>When</th>
                      <th style={th}>Order</th>
                      <th style={{ ...th, textAlign: 'right' }}>Qty</th>
                      <th style={th}>By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.claims.map(c => (
                      <tr key={c.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                        <td style={td}>{fmtDate(c.claimed_at)}</td>
                        <td style={{ ...td, fontFamily: 'monospace' }}>{c.order_number ? fmtInvoiceNo(c.order_number) : '—'}</td>
                        <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: '#0e7490' }}>{c.qty_claimed}</td>
                        <td style={{ ...td, color: '#6b7280' }}>{c.claimed_by_name || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div style={{ padding: '14px 22px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', gap: 8 }}>
              <div>
                {detail.voucher.status === 'ACTIVE' && isAdmin && (
                  <button onClick={() => openVoid(detail.voucher)}
                          style={{ ...btnSecondary, color: '#b91c1c', borderColor: '#fecaca' }}>
                    Void voucher
                  </button>
                )}
              </div>
              <button onClick={() => setDetail(null)} style={btnSecondary}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Void modal */}
      {voidModal && (
        <div style={overlay} onClick={() => !voidModal.saving && setVoidModal(null)}>
          <div style={{ ...modal, maxWidth: 440 }} onClick={e => e.stopPropagation()}>
            <ModalHeader title={`Void ${voidModal.voucher.voucher_number}`}
                         subtitle="This blocks further claims. Existing claims are preserved."
                         onClose={() => setVoidModal(null)} />
            <div style={{ padding: '18px 22px' }}>
              <Field label="Reason *">
                <input type="text" autoFocus value={voidModal.reason}
                       onChange={e => setVoidModal(m => ({ ...m, reason: e.target.value }))}
                       placeholder="e.g. Slip reported lost by customer" style={inp} />
              </Field>
              {voidModal.error && (
                <div style={{ padding: '10px 12px', background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: 8, fontSize: 12 }}>
                  {voidModal.error}
                </div>
              )}
            </div>
            <div style={{ padding: '14px 22px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => !voidModal.saving && setVoidModal(null)} style={btnSecondary}>Cancel</button>
              <button onClick={submitVoid} disabled={voidModal.saving}
                      style={{ ...btnPrimary(voidModal.saving), background: voidModal.saving ? '#9ca3af' : '#dc2626' }}>
                {voidModal.saving ? 'Voiding…' : 'Confirm Void'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 2026-10-21 — admin password confirmation for voiding. */}
      <AdminPasswordPrompt
        open={!!pendingVoid}
        subject={pendingVoid ? `Void voucher ${pendingVoid.voucher.voucher_number}${pendingVoid.reason ? ' — ' + pendingVoid.reason : ''}` : ''}
        actionLabel="Confirm Void"
        onConfirm={confirmVoid}
        onCancel={() => setPendingVoid(null)}
      />
    </div>
  );
}

// ── Style helpers ─────────────────────────────────────────────────
const th = { padding: '11px 14px', textAlign: 'left', fontWeight: 700 };
const td = { padding: '10px 14px', color: '#111827' };
const inp = { width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', background: '#fff' };
const overlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 };
const modal = { background: '#fff', borderRadius: 14, width: '100%' };
const btnSecondary = { padding: '9px 16px', border: '1.5px solid #e5e7eb', borderRadius: 8, background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#374151' };
const btnPrimary = (busy) => ({ padding: '9px 20px', border: 'none', borderRadius: 8, background: busy ? '#9ca3af' : '#16a34a', color: '#fff', cursor: busy ? 'wait' : 'pointer', fontSize: 13, fontWeight: 700 });

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</label>
      {children}
    </div>
  );
}

function ProvField({ label, span, children }) {
  return (
    <div style={{ gridColumn: span ? '1 / -1' : undefined }}>
      <div style={{ fontSize: 10, textTransform: 'uppercase', color: '#64748b', fontWeight: 700, letterSpacing: 0.4, marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 13, color: '#111827', fontWeight: 600 }}>{children}</div>
    </div>
  );
}

function ModalHeader({ title, subtitle, onClose }) {
  return (
    <div style={{ padding: '18px 22px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <div>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#111827' }}>{title}</h3>
        {subtitle && <p style={{ margin: '3px 0 0', fontSize: 12, color: '#6b7280' }}>{subtitle}</p>}
      </div>
      <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}>
        <FiX size={18} />
      </button>
    </div>
  );
}
